import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { toolbarInsertBlockMessages } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import ExpandElementIcon from '@atlaskit/icon-lab/core/expand-element';

import type { ExpandPlugin } from '../types';

type Props = {
	api: ExtractInjectionAPI<ExpandPlugin> | undefined;
};

const nodeName = 'expand';

const ExpandBlockMenuItem = ({ api }: Props) => {
	const { formatMessage } = useIntl();
	const selection = useSharedPluginStateSelector(api, 'selection.selection');

	const isExpandSelected = useMemo(() => {
		if (!selection) {
			return false;
		}

		if (selection instanceof NodeSelection) {
			return selection.node.type.name === nodeName;
		}

		return false;
	}, [selection]);

	const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
		const triggeredFrom =
			event.nativeEvent instanceof KeyboardEvent || event.nativeEvent.detail === 0
				? INPUT_METHOD.KEYBOARD
				: INPUT_METHOD.MOUSE;
		const inputMethod = INPUT_METHOD.BLOCK_MENU;

		api?.core.actions.execute(({ tr }) => {
			const command = api?.blockMenu?.commands.transformNode(tr.doc.type.schema.nodes.expand, {
				inputMethod,
				triggeredFrom,
				targetTypeName: nodeName,
			});
			return command ? command({ tr }) : null;
		});
	};

	return (
		<ToolbarDropdownItem
			onClick={handleClick}
			isSelected={isExpandSelected}
			elemBefore={<ExpandElementIcon label="" />}
		>
			{formatMessage(toolbarInsertBlockMessages.expand)}
		</ToolbarDropdownItem>
	);
};

export const createExpandBlockMenuItem = (api: ExtractInjectionAPI<ExpandPlugin> | undefined) => {
	return () => <ExpandBlockMenuItem api={api} />;
};
